<!--
@component
Search trigger for the docs navbar: opens `DocsSearchPalette` on click, `/`
or Cmd/Ctrl+K. Replaces the Algolia DocSearch widget on the docs site.
-->
<script lang="ts">
  import SearchIcon from "@lucide/svelte/icons/search";

  import { registerOpener } from "$lib/command-palette/open.svelte";
  import {
    type HotkeyConfig,
    useGlobalHotkey,
    useGlobalHotkeyPause,
  } from "$lib/design-systems/product/foundations/global-hotkey.svelte";
  import Button from "$lib/design-systems/product/primitives/buttons/Button.svelte";
  import DocsSearchPalette from "$lib/docs/DocsSearchPalette.svelte";

  type Props = {
    placeholder?: string;
    /** Called after a result is opened, e.g. to close a mobile menu. */
    onClose?: () => void;
  };

  let { placeholder = "Search documentation", onClose }: Props = $props();

  let open = $state(false);

  const HOT_KEY = "/";
  const CMD_K: HotkeyConfig = { key: { ctrlOrCmd: true, hotkey: "k" } };
  const SLASH: HotkeyConfig = { key: { hotkey: HOT_KEY } };

  function toggle() {
    open = !open;
  }

  useGlobalHotkey(CMD_K, toggle);
  useGlobalHotkey(SLASH, () => (open = true));
  // Pause other hotkeys while the palette is open, except the one that closes it.
  useGlobalHotkeyPause({ allowList: [CMD_K.key] }, () => open);
  $effect(() => registerOpener(() => (open = true)));
</script>

<Button
  variant="secondary"
  iconLeading={SearchIcon}
  aria-label={placeholder}
  onclick={() => (open = true)}
  class="p-2 font-normal lg:hidden"
/>
<div class="hidden lg:block">
  <button
    type="button"
    class="mdli-wrapper relative flex w-64 items-center"
    aria-label={placeholder}
    onclick={() => (open = true)}
  >
    <SearchIcon
      size={16}
      class="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 icon-primary"
    />
    <span class="input w-full truncate text-left text-tertiary">
      {placeholder}
    </span>
    <kbd
      class="pointer-events-none absolute top-1/2 right-3 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded bg-secondary text-xs text-secondary"
    >
      {HOT_KEY}
    </kbd>
  </button>
</div>

<DocsSearchPalette bind:open {placeholder} onNavigate={onClose} />

<style lang="postcss">
  @import "$lib/design-systems/product/primitives/inputs/input-styles.css";

  .mdli-wrapper .input {
    @apply pr-9 pl-10;
  }
</style>
